import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import Image from 'components/LegacyImage'
import { useHelpCenterEvents } from '../../events'
import { HelpCenterProduct } from '../../utils'
import classes from './GladlyHelpCenterHub.module.scss'

interface GladlyHelpCenterHubProps {
  products: HelpCenterProduct[]
}

export const GladlyHelpCenterHub = ({ products }: GladlyHelpCenterHubProps) => (
  <div className={`${classes.mainContainer} mc-theme-light mc-w-100`}>
    <Header />
    <div className='container'>
      <ProductExplorer products={products} />
    </div>
  </div>
)

const Header = () => {
  const { t } = useTranslation('@mc/next')

  return (
    <div className={`${classes.header} mc-theme-dark mc-bg-dark`}>
      <h1 className='mc-text-h1 mc-text--center d-flex flex-column align-items-center flex-gap-2'>
        <span className='mc-text-h5 d-block mc-corners--9 mc-px-6 mc-pt-2 mc-pb-2'>
          {t(`helpCenter.GLOBAL.pretitle`)}
        </span>
        {t(`helpCenter.GLOBAL.title`)}
      </h1>
    </div>
  )
}

const ProductExplorer = ({ products }: GladlyHelpCenterHubProps) => {
  // We are using a tags instead of Next's Link component to ensure server navigation between pages
  // This is because:
  // 1. Each help center page has a different Glad App ID and the Glad App third party script doesn't update after initialization
  // 2. For legal reasons we don't want the Decagon script loaded on certain Help Center pages that enterprise customers may visit

  const { trigger } = useHelpCenterEvents()

  const { t } = useTranslation('@mc/next')

  // xl (1200px+): up to 4 per row, avoid orphans
  const getGridCols = (count: number) => {
    if (count <= 4) return count
    if (count === 5 || count === 6) return 3
    return 4
  }

  // lg (960px+): up to 3 per row, avoid orphans
  const getLgGridCols = (count: number) => {
    if (count <= 3) return count
    if (count === 4) return 2
    return 3
  }

  const cols = getGridCols(products.length)
  const lgCols = getLgGridCols(products.length)

  return (
    <div className='mc-pt-10 mc-pb-11 uncontainer'>
      <h2 className='mc-text-h2 mc-text--center mc-mb-9'>
        {t(`helpCenter.GLOBAL.productExplorer.title`)}
      </h2>
      <div
        className={classes.productGrid}
        style={
          {
            '--grid-cols': cols,
            '--grid-cols-lg': lgCols,
          } as React.CSSProperties
        }
      >
        {products.map((product, index) => (
          <a
            href={paths.helpCenterForProduct(product.slug)}
            key={index}
            className={`${classes.productItem} mc-px-7 mc-py-6 mc-pb-9 mc-bg-light d-flex flex-column align-items-center mc-corners--6`}
            onClick={() =>
              trigger('click', {
                location: `Product Tile: ${product.name}`,
                action: 'Open Product Help Center',
              })
            }
          >
            <Image
              src={product.image.src}
              width={120}
              height={112}
              alt={t(`helpCenter.GLOBAL.productExplorer.${product.key}.alt`)}
              className={product.image.className}
            />
            <p className='mc-text--center'>
              {t(
                `helpCenter.GLOBAL.productExplorer.${product.key}.description`,
              )}
            </p>
          </a>
        ))}
      </div>
    </div>
  )
}
